import { forwardRef, memo, Ref, ReactNode, useCallback, useState, CSSProperties, MouseEvent, useEffect } from 'react'
import { trackError } from 'services/analytics'
import Save from '@mui/icons-material/Save'
import LoadingIcon from 'components/LoadingIcon'
import { alpha, useTheme } from '@mui/material/styles'
import Button from './Button'

interface ISubmitButtonProps {
  label?: string
  loadingLabel?: string
  submitting: boolean
  validating: boolean
  submitted: boolean
  type?: 'button' | 'submit' | 'reset'
  icon?: ReactNode
  loadingIcon?: ReactNode
  color?: 'primary' | 'secondary' | 'error'
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void | Promise<void>
  dataCy?: string
  'data-cy'?: string
  disabled?: boolean
  stayDisabledAfterSubmit?: boolean
  style?: CSSProperties
  id?: string
  fullWidth?: boolean
}

function SubmitButton(props: ISubmitButtonProps, ref: Ref<HTMLButtonElement>) {
  const {
    onClick,
    label: labelProp,
    loadingLabel,
    submitting,
    submitted,
    type = 'submit',
    icon = <Save />,
    loadingIcon = <LoadingIcon />,
    dataCy = 'submit',
    disabled: disabledProp,
    stayDisabledAfterSubmit = false,
    color: colorProp,
    id,
    style,
    ...restProps
  } = props

  const [internalSubmitting, setInternalSubmitting] = useState(false)
  const loading = internalSubmitting || submitting
  const disabled = disabledProp || loading || (stayDisabledAfterSubmit && submitted)
  const [buttonDisabled, setButtonDisabled] = useState(disabled)

  // Delay applying the disabled state to the button
  // This is required to avoid the double click submit bug,
  // which is caused by the button getting momentarily disabled just when the user would click it
  useEffect(() => {
    const timeoutHandle = setTimeout(() => {
      setButtonDisabled(disabled || loading)
    }, 100)

    return () => {
      clearTimeout(timeoutHandle)
    }
  }, [loading, disabled])

  const theme = useTheme()

  const updatedStyle = {
    ...style,
    '&:hover': {
      backgroundColor: loading ? `${alpha(theme.palette.primary.main, 0.1)} !important` : '',
    },
  }

  const onClickWrapped = useCallback(async (e: MouseEvent<HTMLButtonElement>) => {
    if (typeof onClick !== 'function') return

    // Prevent default to avoid a form submission in case this is used inside a <form onSubmit=>
    if (type === 'submit') e.preventDefault()

    setInternalSubmitting(true)

    try {
      await onClick(e)
    } catch (error) {
      trackError(error)
    }

    setInternalSubmitting(false)
  }, [onClick, type])

  let label = labelProp || 'Save'

  if (submitting || internalSubmitting) {
    label = loadingLabel || label
  }

  const color = colorProp || 'primary'

  return (
    <Button
      id={id}
      color={color}
      type={type}
      onClick={onClickWrapped}
      ref={ref}
      data-cy={props['data-cy'] || dataCy}
      disabled={buttonDisabled}
      style={updatedStyle}
      startIcon={(loading && buttonDisabled) ? loadingIcon : icon}
      {...restProps}
    >
      {label}
    </Button>
  )
}

export default memo(forwardRef(SubmitButton))
